import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * 🎯 AuthState
 * Estado global para el control de autenticación y sesión del usuario.
 */
export interface AuthState {
  email: string;
  roles: string[];
  permissions: string[];
  sub: string;
  iat: number;
  exp: number;
  accessToken: string | null;
  refreshToken: string | null;
  failedLoginAttempts: number;
  isBlocked: boolean;
  isAuthenticated: boolean;
  error: string;
}

// 🧩 Cargar autenticación previa desde sessionStorage (si existe)
const savedAuth = sessionStorage.getItem("auth");

const initialState: AuthState = savedAuth
  ? JSON.parse(savedAuth)
  : {
      email: "",
      roles: [],
      permissions: [],
      sub: "",
      iat: 0,
      exp: 0,
      accessToken: null,
      refreshToken: null,
      failedLoginAttempts: 0,
      isBlocked: false,
      isAuthenticated: false,
      error: "",
    };

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /**
     * 🔐 Inicia sesión y establece los datos de autenticación
     */
    onLogin: (
      state,
      action: PayloadAction<{
        email: string;
        roles: string[];
        permissions: string[];
        accessToken: string;
        refreshToken: string;
        iat: number;
        exp: number;
      }>
    ) => {
      return {
        ...state,
        ...action.payload,
        failedLoginAttempts: 0,
        isBlocked: false,
        isAuthenticated: true,
        error: "",
      };
    },

    /**
     * 🚪 Cierra sesión y limpia todo el estado
     */
    onLogOut: () => {
      sessionStorage.removeItem("auth");
      return { ...initialState, isAuthenticated: false };
    },

    /**
     * 🔁 Renueva el token de acceso
     */
    onRefreshToken: (
      state,
      action: PayloadAction<{ accessToken: string; exp: number }>
    ) => {
      state.accessToken = action.payload.accessToken;
      state.exp = action.payload.exp;

      sessionStorage.setItem(
        "auth",
        JSON.stringify({
          ...state,
          accessToken: action.payload.accessToken,
          exp: action.payload.exp,
        })
      );
    },

    /**
     * ⚠️ Registra un intento fallido de login
     */
    onFailedLoginAttempt: (state) => {
      state.failedLoginAttempts += 1;
      if (state.failedLoginAttempts >= 5) {
        state.isBlocked = true;
      }
      sessionStorage.setItem("auth", JSON.stringify(state));
    },

    /**
     * 🔓 Desbloquea manualmente un usuario
     */
    onUnlockUser: (state) => {
      state.failedLoginAttempts = 0;
      state.isBlocked = false;
      sessionStorage.setItem("auth", JSON.stringify(state));
    },

    /**
     * ❌ Manejo de errores de autenticación
     */
    setError: (state, action: PayloadAction<string>) => {
      console.error("Auth error:", action.payload);
      state.error = action.payload;
    },

    /**
     * 🧹 Limpia errores actuales
     */
    resetError: (state) => {
      state.error = "";
    },
  },
});

// 🧩 Exportar acciones
export const {
  onLogin,
  onLogOut,
  onRefreshToken,
  onFailedLoginAttempt,
  onUnlockUser,
  setError,
  resetError,
} = authSlice.actions;

// 🧩 Exportar reducer
export default authSlice.reducer;
